import { useClient, useItemContext, useQuery } from '@weenat/client'
import { HistoryState, getDefaultHistoryState } from '@weenat/client/dist/HistoryContext'
import { isVirtualWeatherStation } from '@weenat/client/dist/core/devices'
import {
  AllowedMetricsInHistory,
  PredefinedPeriodIds,
  getMetricsToFetch
} from '@weenat/client/dist/core/history/history'
import { UseMetricSummaryChartInterface } from '@weenat/client/dist/core/metrics/useMetricSummaryChart'
import { BillingStatus } from '@weenat/client/dist/enums'
import { TimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import { usePathname } from 'app/routx-router'
import PeriodBasedMetricSummary from 'app/src/dashboard/components/common/summary/dailyWeather/PeriodBasedMetricSummary'
import { PREVENT_DRAG_CLASSNAME } from 'app/src/dashboard/components/plots/dashboards/constants'
import { DashboardHistoryPeriodState } from 'app/src/devices/types'
import { useGlobalHistoryState, useToggleFeature } from 'app/state'
import { isArray, isNil } from 'lodash-es'
import moment from 'moment-timezone'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMeasure } from 'react-use'
import DelimitedFlex from '../DelimitedFlex'
import Text from '../Text'
import { MetricChart } from '../charts/metrics/MetricSummaryChart'
import MetricIcons from '../icons/MetricIcons'
import WeedgetCard from './WeedgetCard'
import WeedgetFooter from './WeedgetFooter'
import WeedgetHeader from './WeedgetHeader'
import WeedgetHeaderSelect from './WeedgetHeaderSelect'
import WeedgetLoading from './WeedgetLoading'

type OptionValues = 'D' | 'DD' | '7D'

interface MetricWeedgetProps {
  /**
   * When provided, the card is controlled by an external period (e.g. the
   * shared selector above the "Récemment" section under the `sdh` flag).
   * The per-card period selector is hidden in that case.
   */
  period?: DashboardHistoryPeriodState
  metric: AllowedMetricsInHistory
}

const MetricWeedget: FC<MetricWeedgetProps> = ({ metric, period: controlledPeriod }) => {
  const { t } = useIntl()
  const client = useClient()
  const { id, timezone } = useItemContext()
  const org = useOrgContext()
  const pathname = usePathname()
  const nav = useNavigate()
  const [{ sdh }] = useToggleFeature()
  const isControlled = !isNil(controlledPeriod)

  const MetricIcon = MetricIcons[metric]
  const [ref, { height }] = useMeasure<HTMLDivElement>()

  const [, setGlobalHistoryState] = useGlobalHistoryState()

  const options: Array<{
    label: string
    value: OptionValues
    period: DashboardHistoryPeriodState
  }> = useMemo(
    () => [
      {
        label: t('periods.today', { capitalize: true }),
        value: 'D',
        period: {
          key: 'D',
          span: TimeSteps.perHours,
          since: moment.tz(timezone).startOf('day').unix(),
          before: moment.tz(timezone).startOf('day').add(1, 'day').unix()
        }
      },
      {
        label: t('periods.last_day', { capitalize: true }),
        value: 'DD',
        period: {
          key: 'DD',
          span: TimeSteps.perHours,
          since: moment.tz(timezone).startOf('day').add(-1, 'day').unix(),
          before: moment.tz(timezone).startOf('day').unix()
        }
      },
      {
        label: t('periods.last_7_days', { capitalize: true }),
        value: '7D',
        period: {
          key: '7D',
          span: TimeSteps.perHours,
          since: moment.tz(timezone).startOf('day').add(-6, 'day').unix(),
          before: moment.tz(timezone).startOf('day').add(1, 'day').unix()
        }
      }
    ],
    [t, timezone]
  )

  const [selectedValue, setSelectedValue] = useState<OptionValues>('D')

  const selectedPeriod = useMemo(
    () =>
      isControlled
        ? controlledPeriod
        : options.find((option) => option.value === selectedValue)!.period,
    [isControlled, controlledPeriod, options, selectedValue]
  )

  const metricToPrefetch = getMetricsToFetch([metric])

  const deviceMeasurementsRequest = useQuery(
    client.measurements.devices.histories.get1_1({
      deviceId: id,
      ...selectedPeriod,
      metrics: metricToPrefetch
    }),
    {
      enabled: !isNil(selectedPeriod)
    }
  )

  const deviceRequest = useQuery(client.devices.get(id), { enabled: !isNil(id) })
  const device = deviceRequest.data

  const prefetch: UseMetricSummaryChartInterface['prefetch'] = {
    isLoading: deviceMeasurementsRequest.isLoading,
    measurements: deviceMeasurementsRequest.data
  }

  const isExpertOrg = org.billingStatus === BillingStatus.expert

  return (
    <WeedgetCard $position='relative' $flex={1}>
      {/*  HEADER */}
      <WeedgetHeader>
        <Flex $alignItems='center' $flex={1} $minWidth={0} $gap={'md'}>
          <MetricIcon
            $size='lg'
            $backgroundColor={`metrics.${metric}.500`}
            $p='sm'
            $color='grayscale.white'
          />
          <Flex $flexDirection='column' $flex={1} $minWidth={0}>
            <Text $fontWeight='bold' $ellipsis>
              {t(`metrics.${metric}`, { capitalize: true })}
            </Text>
          </Flex>
        </Flex>
        {sdh ? null : (
          <WeedgetHeaderSelect<OptionValues>
            value={selectedValue}
            name={'period_selector'}
            onChange={(value) => {
              if (isNil(value) || isArray(value)) return
              setSelectedValue(value)
            }}
            options={options}
            label={undefined}
            hideErrors
            className={PREVENT_DRAG_CLASSNAME}
          />
        )}
      </WeedgetHeader>

      {/* CONTENT */}
      <Flex $flexDirection='column' $flex={1}>
        {isNil(device) || isNil(prefetch) ? (
          <WeedgetLoading />
        ) : (
          <>
            {/* AGGREGATES */}
            <DelimitedFlex $gap='lg' $px='md' $pt='md' $pb='sm'>
              <PeriodBasedMetricSummary
                resource={device}
                metric={metric}
                prefetch={prefetch}
                period={selectedPeriod}
                isExpertOrg={isExpertOrg}
              />
            </DelimitedFlex>
            {/* CHART */}
            <Flex $flex={1} ref={ref} $flexDirection='column'>
              <MetricChart
                metric={metric}
                prefetch={prefetch}
                height={height}
                variant='sm'
                isVWS={isVirtualWeatherStation(device)}
              />
            </Flex>
          </>
        )}
      </Flex>

      {/* FOOTER */}
      <WeedgetFooter
        destination={() => {
          if (!isNil(device)) {
            const newHistoryState: Partial<HistoryState> = {
              ...getDefaultHistoryState(device.timezone),
              selectedMetrics: [metric],
              since: selectedPeriod.since,
              before: selectedPeriod.before,
              selectedPeriodId: 'custom' as PredefinedPeriodIds,
              selectedTimeStep: selectedPeriod.span
            }
            setGlobalHistoryState(newHistoryState)
          }
          nav(`${pathname}/history`, { replace: true })
        }}
        label={t('dashboards.metrics.see_more', { capitalize: true })}
      />
    </WeedgetCard>
  )
}

export default MetricWeedget
